// SmartLocksControl.jsx



import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemAvatar,
  Avatar,
  CircularProgress,
  TextField,
  IconButton,
  Menu,
  MenuItem,
  Checkbox,
  Divider,
  Tooltip,
  useMediaQuery,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";

import MoreVertIcon from "@mui/icons-material/MoreVert";
import EditIcon from "@mui/icons-material/Edit";
import LockIcon from "@mui/icons-material/Lock";
import LockOpenIcon from "@mui/icons-material/LockOpen";
import BatteryFullIcon from "@mui/icons-material/BatteryFull";
import BatteryAlertIcon from "@mui/icons-material/BatteryAlert";
import FiberManualRecordIcon from "@mui/icons-material/FiberManualRecord";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";

import { useAuth } from "../../hooks/useAuth";
import AccessCodeManager from "./AccessCodeManager";
import AccessLogs from "../dashboard/AccessLogs";

import {
  loadLocks,
  pollLocks,
  connectWebview,
  handleLockAction,
  handleUpdateLockName,
  openAccessCodeDialog,
  closeAccessCodeDialog,
  openLogsDialog,
  closeLogsDialog,
  isMatchingLock,
  formatLockName,
  parseLockName,
  handleTransferLocks,
} from "./smartLocksHelpers";

// ⬇️ Using the styles file below
import styles from "./DeviceList.styles";
import useSmartLockSubscriptionGuard from "../../hooks/useSmartLockSubscriptionGuard";

const POLLING_INTERVAL_MS = 5000;

const SmartLocksControl = ({ agentId, accessCodeData }) => {
  const theme = useTheme();
  const s = styles(theme);
  const isSmDown = useMediaQuery(theme.breakpoints.down("sm"));

  const { currentUser } = useAuth();
  const { requireSubscription, subscriptionGuardLoading, modal } = useSmartLockSubscriptionGuard();

  // ---------- State ----------
  const [locks, setLocks] = useState([]);
  const [iframeUrl, setIframeUrl] = useState("");
  const [showIframe, setShowIframe] = useState(false);

  const [openAccessCodeDialogState, setOpenAccessCodeDialog] = useState(false);
  const [selectedLockId, setSelectedLockId] = useState(null);

  const [lockActionLoading, setLockActionLoading] = useState({});
  const [openLogsForLockId, setOpenLogsForLockId] = useState(null);

  // Menu
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [selectedLock, setSelectedLock] = useState(null);

  // Rename
  const [renameDialogOpen, setRenameDialogOpen] = useState(false);
  const [renameLockDetails, setRenameLockDetails] = useState({
    address: "",
    city: "",
    state: "",
    zip: "",
    lockName: "",
  });
  const [renameLockId, setRenameLockId] = useState(null);
  const [isRenaming, setIsRenaming] = useState(false);

  // Selection + transfer
  const [selectedLockIds, setSelectedLockIds] = useState(new Set());
  const [selectAllChecked, setSelectAllChecked] = useState(false);
  const [transferDialogOpen, setTransferDialogOpen] = useState(false);
  const [transferEmail, setTransferEmail] = useState("");
  const [transferLoading, setTransferLoading] = useState(false);

  // ---------- Effects ----------
  useEffect(() => {
    loadLocks(currentUser, setLocks);
  }, [currentUser]);

  useEffect(() => {
    const id = setInterval(() => {
      pollLocks(currentUser, locks, setLocks);
    }, POLLING_INTERVAL_MS);
    return () => clearInterval(id);
  }, [locks, currentUser]);

  useEffect(() => {
    if (!locks.length) {
      setSelectedLockIds(new Set());
      setSelectAllChecked(false);
    } else {
      setSelectAllChecked(
        selectedLockIds.size > 0 && selectedLockIds.size === locks.length
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locks]);

  // ---------- Menu ----------
  const handleMenuClick = (event, lock) => {
    event.stopPropagation();
    setMenuAnchor(event.currentTarget);
    setSelectedLock(lock);
  };
  const handleMenuClose = () => {
    setMenuAnchor(null);
    setSelectedLock(null);
  };

  // ---------- Rename ----------
  const handleOpenRenameDialog = (lock) => {
    setRenameDialogOpen(true);
    setRenameLockId(lock.lockId);
    
    // Parse the deviceName to extract address components and lock name
    const parsed = parseLockName(lock.deviceName || "");
    setRenameLockDetails({
      address: parsed.address,
      city: parsed.city,
      state: parsed.state,
      zip: parsed.zip,
      lockName: parsed.lockName,
    });
  };
  const handleCloseRenameDialog = () => {
    setRenameDialogOpen(false);
    setRenameLockId(null);
    setRenameLockDetails({
      address: "",
      city: "",
      state: "",
      zip: "",
      lockName: "",
    });
  };
  const handleConfirmRename = async () => {
    if (!renameLockId) return;
    try {
      const formattedLockName = formatLockName(renameLockDetails);
      setIsRenaming(true);
      
      // Update the lock name via API
      await handleUpdateLockName(
        renameLockId,
        formattedLockName,
        currentUser,
        setLocks,
        setIsRenaming
      );
      
      // Reload locks from backend to ensure we have the latest data
      // Wait a moment for backend to process, then reload
      await new Promise(resolve => setTimeout(resolve, 500));
      loadLocks(currentUser, setLocks);
      
      handleCloseRenameDialog();
    } catch (error) {
      console.error("Rename error:", error);
      alert(error.message || "Failed to rename lock. Please try again.");
      setIsRenaming(false);
    }
  };

  // ---------- Selection & Transfer ----------
  const toggleSelectLock = (lockId) => {
    setSelectedLockIds((prev) => {
      const next = new Set(prev);
      next.has(lockId) ? next.delete(lockId) : next.add(lockId);
      return next;
    });
  };

  const toggleSelectAll = () => {
    setSelectAllChecked((prev) => {
      const nextVal = !prev;
      if (nextVal) setSelectedLockIds(new Set(locks.map((l) => l.lockId)));
      else setSelectedLockIds(new Set());
      return nextVal;
    });
  };

  const openTransferDialogFor = (lockIds) => {
    setSelectedLockIds(new Set(lockIds));
    setTransferDialogOpen(true);
  };

  const handleConfirmTransfer = async () => {
    if (!transferEmail || selectedLockIds.size === 0) return;
    try {
      setTransferLoading(true);
      await handleTransferLocks(
        Array.from(selectedLockIds),
        transferEmail,
        currentUser
      );
      setTransferLoading(false);
      setTransferDialogOpen(false);
      setTransferEmail("");
      setSelectedLockIds(new Set());
      setSelectAllChecked(false);
    } catch (err) {
      console.error("Transfer failed", err);
      setTransferLoading(false);
    }
  };

  const currentLockName =
    locks.find((lk) => lk.lockId === selectedLockId)?.deviceName ?? "";

  // ---------- Render ----------
  return (
    <Box sx={s.container}>
      {/* Header */}
      <Box sx={s.headerWrap}>
        <Box sx={s.headerLeft}>
          <Typography variant="h6" sx={s.title}>
            Connected Devices <Box component="span" sx={s.deviceCount}>{locks.length}</Box>
          </Typography>

          <Box sx={s.bulkRow}>
            <Checkbox
              checked={selectAllChecked}
              onChange={toggleSelectAll}
              size="small"
              inputProps={{ "aria-label": "Select all locks" }}
            />
            <Typography variant="body2" noWrap>
              Select all
            </Typography>
            <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />
            <Button
              size="small"
              variant="outlined"
              startIcon={<SwapHorizIcon />}
              onClick={() => openTransferDialogFor(Array.from(selectedLockIds))}
              disabled={selectedLockIds.size === 0}
              sx={s.transferBtn}
            >
              Transfer selected ({selectedLockIds.size || 0})
            </Button>
          </Box>
        </Box>

        <Button
          variant="contained"
          sx={s.addBtn}
          onClick={() =>
            requireSubscription(() => connectWebview(currentUser, setIframeUrl, setShowIframe))
          }
          disabled={subscriptionGuardLoading}
        >
          + Add Device Account
        </Button>
      </Box>

      {/* Context note */}
      {accessCodeData && (
        <Box sx={s.cardNote}>
          <Typography variant="body2" color="primary">
            You’ve approved a viewing for{" "}
            <strong>{accessCodeData.address}</strong>. Select a lock to create
            an access code. Matching locks are highlighted.
          </Typography>
        </Box>
      )}

      {/* Locks */}
      <List sx={s.list}>
        {locks.map((lock) => {
          const highlighted = isMatchingLock(lock, accessCodeData);

          const desktopActions = (
            <Box sx={s.actionsRight}>
              {/* Desktop row Transfer (unchanged) */}
              <Button
                size="small"
                variant="text"
                startIcon={<SwapHorizIcon />}
                onClick={(e) => {
                  e.stopPropagation();
                  openTransferDialogFor([lock.lockId]);
                }}
                sx={s.transferBtnCard}
              >
                Transfer
              </Button>
              <IconButton
                onClick={(event) => handleMenuClick(event, lock)}
                sx={s.menuBtn}
                aria-label="Open actions"
              >
                <MoreVertIcon />
              </IconButton>
            </Box>
          );

          return (
            <ListItem
              key={lock.lockId}
              onClick={() => {
                requireSubscription(() => {
                  setSelectedLockId(lock.lockId);
                  setOpenAccessCodeDialog(true);
                });
              }}
              sx={{
                ...s.row,
                ...(highlighted ? s.rowHighlighted : {}),
                cursor: accessCodeData ? "pointer" : "default",
              }}
              secondaryAction={!isSmDown ? desktopActions : undefined}
            >
              {/* Select */}
              <Box sx={s.selectBox} onClick={(e) => e.stopPropagation()}>
                <Checkbox
                  checked={selectedLockIds.has(lock.lockId)}
                  onChange={() => toggleSelectLock(lock.lockId)}
                  size="small"
                  inputProps={{
                    "aria-label": `Select ${lock.deviceName || "lock"}`,
                  }}
                />
              </Box>

              {/* Info */}
              <Box sx={s.infoWrap}>
                <ListItemAvatar sx={{ minWidth: 44 }}>
                  <Avatar
                    src={lock.imageUrl}
                    alt={lock.deviceName}
                    sx={s.avatar}
                  />
                </ListItemAvatar>
                <Box sx={s.nameBlock}>
                  <Tooltip title={lock.deviceName || ""} arrow>
                    <Typography variant="subtitle1" sx={s.deviceName}>
                      {lock.deviceName || "Unnamed Lock"}
                    </Typography>
                  </Tooltip>
                  <Typography variant="caption" sx={s.deviceType}>
                    {lock.deviceType || "August"}
                  </Typography>
                </Box>
              </Box>

              {/* Meta (status pills) */}
              <Box sx={s.metaWrap}>
                {/* Lock status */}
                <Box sx={s.metaPill}>
                  {lockActionLoading[lock.lockId] ? (
                    <CircularProgress size={18} sx={{ mr: 0.5 }} />
                  ) : lock.isLocked ? (
                    <LockIcon className="locked" sx={s.lockIcon} />
                  ) : (
                    <LockOpenIcon className="unlocked" sx={s.lockIcon} />
                  )}
                  <Typography variant="body2" sx={s.metaText}>
                    {lock.isLocked ? "Locked" : "Unlocked"}
                  </Typography>
                </Box>

                {/* Online */}
                <Box sx={s.metaPill}>
                  <FiberManualRecordIcon
                    sx={[
                      s.dotOnline,
                      (lock.online === false || lock.isOnline === false) && { color: "#333333" },
                    ]}
                  />
                  <Typography variant="body2" sx={s.metaText}>
                    {(lock.online === false || lock.isOnline === false) ? "Offline" : "Online"}
                  </Typography>
                </Box>

                {/* Battery */}
                <Box sx={s.metaPill}>
                  {lock.batteryLevel && lock.batteryLevel > 0.2 ? (
                    <BatteryFullIcon sx={s.batteryOk} />
                  ) : (
                    <BatteryAlertIcon sx={s.batteryLow} />
                  )}
                  <Typography variant="body2" sx={s.metaText}>
                    {lock.batteryLevel
                      ? `${(lock.batteryLevel * 100).toFixed(0)}%`
                      : "0%"}
                  </Typography>
                </Box>
              </Box>

              {/* Inline actions on mobile — NO Transfer here */}
              {isSmDown && (
                <Box
                  sx={s.actionsInlineMobile}
                  onClick={(e) => e.stopPropagation()}
                >
                  <Tooltip title={lock.isLocked ? "Unlock" : "Lock"}>
                    <span>
                      <IconButton
                        aria-label={lock.isLocked ? "Unlock" : "Lock"}
                        onClick={() =>
                          requireSubscription(() =>
                            handleLockAction(
                              lock.lockId,
                              lock.isLocked ? "unlock" : "lock",
                              currentUser,
                              setLockActionLoading,
                              locks,
                              setLocks
                            )
                          )
                        }
                        disabled={Boolean(lockActionLoading[lock.lockId])}
                        size="small"
                      >
                        {lock.isLocked ? <LockOpenIcon /> : <LockIcon />}
                      </IconButton>
                    </span>
                  </Tooltip>

                  {/* Note: Transfer button removed on small screens */}

                  <IconButton
                    onClick={(event) => handleMenuClick(event, lock)}
                    aria-label="Open actions"
                    size="small"
                  >
                    <MoreVertIcon />
                  </IconButton>
                </Box>
              )}
            </ListItem>
          );
        })}
      </List>

      {/* Actions Menu (Transfer stays available on all screen sizes) */}
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={handleMenuClose}
      >
        <MenuItem
          onClick={() => {
            if (!selectedLock) return;
            requireSubscription(() => {
              handleLockAction(
                selectedLock.lockId,
                selectedLock.isLocked ? "unlock" : "lock",
                currentUser,
                setLockActionLoading,
                locks,
                setLocks
              );
              handleMenuClose();
            });
          }}
        >
          {selectedLock?.isLocked ? <LockOpenIcon /> : <LockIcon />}
          &nbsp;{selectedLock?.isLocked ? "Unlock" : "Lock"}
        </MenuItem>
        <MenuItem
          onClick={() => {
            if (!selectedLock) return;
            requireSubscription(() => {
              openAccessCodeDialog(
                selectedLock.lockId,
                setSelectedLockId,
                setOpenAccessCodeDialog
              );
              handleMenuClose();
            });
          }}
        >
          Manage Access Codes
        </MenuItem>
        <MenuItem
          onClick={() => {
            if (!selectedLock) return;
            requireSubscription(() => {
              openLogsDialog(selectedLock.lockId, setOpenLogsForLockId);
              handleMenuClose();
            });
          }}
        >
          View Logs
        </MenuItem>
        <MenuItem
          onClick={() => {
            if (!selectedLock) return;
            requireSubscription(() => {
              handleMenuClose();
              handleOpenRenameDialog(selectedLock);
            });
          }}
        >
          <EditIcon sx={{ mr: 1 }} />
          Rename Lock
        </MenuItem>
        <MenuItem
          onClick={() => {
            if (!selectedLock) return;
            requireSubscription(() => {
              handleMenuClose();
              openTransferDialogFor([selectedLock.lockId]);
            });
          }}
        >
          <SwapHorizIcon sx={{ mr: 1 }} />
          Transfer Lock
        </MenuItem>
      </Menu>

      {/* Seam Connect Webview */}
      <Dialog
        open={showIframe}
        onClose={() => setShowIframe(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>Connect Smart Lock</DialogTitle>
        <DialogContent>
          <iframe
            src={iframeUrl}
            width="100%"
            height="600px"
            style={{ border: "none" }}
            title="Seam Connect Webview"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowIframe(false)} color="secondary">
            Close
          </Button>
        </DialogActions>
      </Dialog>
      {modal}

      {/* Rename */}
      <Dialog
        open={renameDialogOpen}
        onClose={handleCloseRenameDialog}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Rename Lock</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Address"
            margin="dense"
            value={renameLockDetails.address}
            onChange={(e) =>
              setRenameLockDetails((p) => ({ ...p, address: e.target.value }))
            }
          />
          <TextField
            fullWidth
            label="City"
            margin="dense"
            value={renameLockDetails.city}
            onChange={(e) =>
              setRenameLockDetails((p) => ({ ...p, city: e.target.value }))
            }
          />
          <TextField
            fullWidth
            label="State"
            margin="dense"
            value={renameLockDetails.state}
            onChange={(e) =>
              setRenameLockDetails((p) => ({ ...p, state: e.target.value }))
            }
          />
          <TextField
            fullWidth
            label="ZIP Code"
            margin="dense"
            value={renameLockDetails.zip}
            onChange={(e) =>
              setRenameLockDetails((p) => ({ ...p, zip: e.target.value }))
            }
          />
          <TextField
            fullWidth
            label="Lock Name"
            margin="dense"
            value={renameLockDetails.lockName}
            onChange={(e) =>
              setRenameLockDetails((p) => ({ ...p, lockName: e.target.value }))
            }
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseRenameDialog} color="secondary">
            Cancel
          </Button>
          <Button
            onClick={handleConfirmRename}
            variant="contained"
            disabled={isRenaming}
          >
            {isRenaming ? <CircularProgress size={20} /> : "Save"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Transfer */}
      <Dialog
        open={transferDialogOpen}
        onClose={() => setTransferDialogOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>
          Transfer{" "}
          {selectedLockIds.size > 1
            ? `${selectedLockIds.size} locks`
            : "lock"}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 1 }}>
            Enter the email of the user you want to transfer{" "}
            {selectedLockIds.size > 1 ? "these locks" : "this lock"} to.
          </Typography>
          <TextField
            autoFocus
            fullWidth
            type="email"
            label="Recipient Email"
            placeholder="name@example.com"
            margin="dense"
            value={transferEmail}
            onChange={(e) => setTransferEmail(e.target.value)}
          />
          {selectedLockIds.size > 0 && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="caption" color="text.secondary">
                Locks to transfer:
              </Typography>
              <List dense sx={{ maxHeight: 160, overflow: "auto" }}>
                {Array.from(selectedLockIds).map((id) => {
                  const lk = locks.find((l) => l.lockId === id);
                  return (
                    <ListItem key={id} sx={{ py: 0 }}>
                      <Typography variant="body2">
                        {lk?.deviceName || id}
                      </Typography>
                    </ListItem>
                  );
                })}
              </List>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTransferDialogOpen(false)} color="secondary">
            Cancel
          </Button>
          <Button
            onClick={handleConfirmTransfer}
            variant="contained"
            startIcon={!transferLoading && <SwapHorizIcon />}
            disabled={
              !transferEmail || selectedLockIds.size === 0 || transferLoading
            }
          >
            {transferLoading ? <CircularProgress size={20} /> : "Confirm Transfer"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Access Code Manager */}
      <AccessCodeManager
        isOpen={openAccessCodeDialogState}
        onClose={() => closeAccessCodeDialog(setOpenAccessCodeDialog)}
        uid={currentUser?.uid}
        deviceType={selectedLockId ? locks.find((l) => l.lockId === selectedLockId)?.deviceType : undefined}
        lockId={selectedLockId}
        lockName={currentLockName}
        initialEmail={accessCodeData?.email}
        initialPhone={accessCodeData?.phone}
        initialStartsAt={accessCodeData?.startsAt}
        initialEndsAt={accessCodeData?.endsAt}
        initialAddress={accessCodeData?.address}
      />

      {/* Logs */}
      <Dialog
        open={Boolean(openLogsForLockId)}
        onClose={() => closeLogsDialog(setOpenLogsForLockId)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Access Logs</DialogTitle>
        <DialogContent dividers>
          {openLogsForLockId && <AccessLogs lockId={openLogsForLockId} />}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => closeLogsDialog(setOpenLogsForLockId)}
            color="secondary"
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SmartLocksControl;
